import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';

function Preferences() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    numero_tva: '',
    societe_code_postal: '',
    societe_ville: '',
    societe_pays: 'Belgique',
    peppol_endpoint_scheme: '',
    peppol_endpoint_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Vérifier si l'utilisateur est administrateur ou superadmin
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'superadmin') {
      setMessage({ type: 'error', text: 'Accès refusé. Seuls les administrateurs peuvent modifier les préférences.' });
      setTimeout(() => {
        navigate('/');
      }, 3000);
      return;
    }
    loadPreferences();
  }, [user, navigate]);

  const loadPreferences = async () => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/preferences', {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData((prev) => ({ ...prev, ...data }));
      } else if (response.status === 404) {
        // Pas de préférences existantes, on garde les valeurs par défaut
        console.log('Aucune préférence trouvée, utilisation des valeurs par défaut');
      } else {
        throw new Error('Erreur lors du chargement des préférences');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des préférences' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (user?.role !== 'admin' && user?.role !== 'superadmin') return;
    
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/api/preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Préférences sauvegardées avec succès' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage({ type: 'error', text: error.message || 'Erreur lors de la sauvegarde des préférences' });
    } finally {
      setSaving(false);
    }
  };

  // Si l'utilisateur n'est pas administrateur ou superadmin, afficher un message d'erreur
  if (user && user.role !== 'admin' && user.role !== 'superadmin') {
    return (
      <Box sx={{ pb: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Accès refusé. Seuls les administrateurs peuvent modifier les préférences de l'entreprise.
        </Alert>
        <Typography variant="body1">
          Redirection vers la page d'accueil...
        </Typography>
        <Footer />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Préférences de la société
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configurez les coordonnées de votre société qui apparaîtront sur vos factures.
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de la société"
                value={formData.nom}
                onChange={handleInputChange('nom')}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro de TVA"
                value={formData.numero_tva}
                onChange={handleInputChange('numero_tva')}
                variant="outlined"
                placeholder="BE0123456789"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.adresse}
                onChange={handleInputChange('adresse')}
                required
                variant="outlined"
                multiline
                rows={3}
                placeholder="Rue de la Colo 1, 0000 Cheveux"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={handleInputChange('telephone')}
                variant="outlined"
                placeholder="+32 123 45 67 89"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                variant="outlined"
                placeholder="contact@woudcoiff.be"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2 }}>
                E-facturation PEPPOL (fournisseur)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Adresse structurée et identifiants requis pour générer un UBL conforme aux contrôles de l’application.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Code postal (société)"
                value={formData.societe_code_postal || ''}
                onChange={handleInputChange('societe_code_postal')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Ville (société)"
                value={formData.societe_ville || ''}
                onChange={handleInputChange('societe_ville')}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pays (société)"
                value={formData.societe_pays || ''}
                onChange={handleInputChange('societe_pays')}
                variant="outlined"
                placeholder="Belgique"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Schéma PEPPOL fournisseur"
                value={formData.peppol_endpoint_scheme || ''}
                onChange={handleInputChange('peppol_endpoint_scheme')}
                variant="outlined"
                placeholder="ex: 0208"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Identifiant PEPPOL fournisseur"
                value={formData.peppol_endpoint_id || ''}
                onChange={handleInputChange('peppol_endpoint_id')}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Les coordonnées PEPPOL des <strong>maisons de repos</strong> (factures groupées Les Charmilles / Notre
                foyer) se saisissent dans l’onglet{' '}
                <Button size="small" variant="outlined" onClick={() => navigate('/maisons-repos')}>
                  Maison de repos
                </Button>
                .
              </Alert>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
              sx={{ minWidth: 120 }}
            >
              {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
            </Button>
          </Box>
        </Box>
      </Paper>
      
      <Footer />
    </Box>
  );
}

export default Preferences;
